import React, {useContext, useEffect, useState} from 'react';
import {Select, DatePicker, Button as AntdButton, Form, Switch} from 'antd';
import dayjs from 'dayjs';
import axios from "@common/axios";
import {ArrowForwardRounded, AutoAwesomeRounded, GradingRounded} from "@mui/icons-material";
import {UserContext} from "@context/UserContext";
import {useLocation, useSearchParams} from "wouter";
import TableAntD from "@common/TableAntD";
import {useBreadcrumbComponent} from "@common/useBreadcrumbComponent";
import ExportToCsvButton from '@common/ExportToCsvButton';
import TableFilterControlsForm from "@common/TableFilterControlsForm";
import TableFilterControls from "@common/TableFilterControls";
import TableFilterControlsSection from "@common/TableFilterControlsSection";
import CircularLoader from "@common/CircularLoader";
import {BrandContext} from "@context/BrandContext";
import Button from "@common/Button";
import GenerateActivityReportSummaryDialog from "./activityReportSummaries/GenerateActivityReportSummaryDialog";
import PageHeader from "@common/PageHeader";

const { RangePicker } = DatePicker;

const ActivityReport = ({ classIdParam }) => {
    const [loading, setLoading] = useState(true);
    const { user } = useContext(UserContext);
    const brandConfig = useContext(BrandContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const [dateRange, setDateRange] = useState(() => {
        const startParam = searchParams.get('startDate');
        const endParam = searchParams.get('endDate');

        if (startParam && endParam) {
            return [dayjs(startParam), dayjs(endParam)];
        }
        return [dayjs().subtract(6, 'day'), dayjs()];
    });
    const [selectedClass, setSelectedClass] = useState();
    const [showAllClasses, setShowAllClasses] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState(() => {
        const defaultColumns = [
            'student',
            'sessionsStarted',
            'lastLogin',
            'exercisesComplete',
            'walkthroughsComplete',
            'quizzesComplete',
            'quizQuestions',
            'testsComplete',
            'scorchersComplete'
        ];

        if (brandConfig.brandCode === 'DYNAMICENGLISH') {
            return defaultColumns.filter(col =>
                col !== 'exercisesComplete' && col !== 'walkthroughsComplete'
            );
        }

        return defaultColumns;
    });
    const [reportData, setReportData] = useState([])
    const [teacherClasses, setTeacherClasses] = useState([])
    const [allSchoolClasses, setAllSchoolClasses] = useState([])
    const [studentFilters, setStudentFilters] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [location, navigate] = useLocation();
    const [isAIReportGeneratorDialogVisible, setAIReportGeneratorDialogVisible] = useState(false);

    const presetRanges = {
        Today: [dayjs(), dayjs()],
        'This Week': [dayjs().startOf('week'), dayjs().endOf('week')],
        'Last 7 Days': [dayjs().subtract(6, 'day'), dayjs()],
        'This Month': [dayjs().startOf('month'), dayjs().endOf('month')],
    };

    const columnOptions = [
        { label: 'Sessions Started', value: 'sessionsStarted' },
        { label: 'Last Login', value: 'lastLogin' },
        { label: 'Exercises Complete', value: 'exercisesComplete' },
        { label: 'Walkthroughs Complete', value: 'walkthroughsComplete' },
        { label: 'Section Quizzes Complete', value: 'quizzesComplete' },
        { label: 'Questions Complete', value: 'quizQuestions' },
        { label: `${brandConfig.testsName} Complete`, value: 'testsComplete' },
        { label: 'Scorchers Complete', value: 'scorchersComplete'}
    ].filter(option => {
        if (brandConfig.brandCode === 'DYNAMICENGLISH') {
            return option.value !== 'exercisesComplete' && option.value !== 'walkthroughsComplete';
        }
        return true;
    });

    const loadClasses = async () => {
        try {
            const response = await fetch('/controlCentre/getTeacherClasses.action');
            const data = await response.json();

            const classOptions = data.classes.map(cls => ({
                label: cls.className,
                value: cls.classId,
            }));

            setTeacherClasses(classOptions);

            const classId = classIdParam;

            if (classId) {
                const parsedClassId = parseInt(classId);
                const classExists = classOptions.some(option => option.value === parsedClassId);

                if (classExists) {
                    setSelectedClass(parsedClassId);
                }
            }
        } catch (error) {
            console.error('Failed to load teacher classes:', error);
        }
    };

    const loadAllSchoolClasses = async () => {
        try {
            const response = await fetch('/web/ui/sc/classes');
            const data = await response.json();

            const classOptions = Object.values(data).map(cls => ({
                label: cls.className,
                value: cls.classId || cls.id,
            }));

            setAllSchoolClasses(classOptions);
        } catch (error) {
            console.error('Failed to load all school classes:', error);
        }
    };

    const loadReportData = () => {
        setLoading(true);
        const [start, end] = dateRange || [];

        const isUSLocale = user.locale === 'en_US';

        let formattedStart, formattedEnd;

        if (start) {
            const day = start.date().toString().padStart(2, '0');
            const month = (start.month() + 1).toString().padStart(2, '0');
            const year = start.year();
            formattedStart = isUSLocale ? `${month}/${day}/${year}` : `${day}/${month}/${year}`;
        }

        if (end) {
            const day = end.date().toString().padStart(2, '0');
            const month = (end.month() + 1).toString().padStart(2, '0');
            const year = end.year();
            formattedEnd = isUSLocale ? `${month}/${day}/${year}` : `${day}/${month}/${year}`;
        }

        axios.get('/web/ui/report/teacher/activity/classReport', {
            params: {
                classId: selectedClass,
                ...(formattedStart && { startSearch: formattedStart }),
                ...(formattedEnd && { endSearch: formattedEnd }),
                ...((formattedStart && formattedEnd) && { searchPeriod: 'DATE_RANGE' })
            }
        })
            .then(response => {
                if (response.data) {
                    setReportData(response.data?.reportData);
                } else {
                    setReportData([])
                    console.error('Failed to load report: No data received');
                }
            })
            .catch(error => {
                console.error('Error fetching report:', error);
            })
            .finally(() => {
                setLoading(false)
            });
    };

    useEffect(() => {
        loadClasses();
        setLoading(false);
    }, []);

    useEffect(() => {
        if (showAllClasses) {
            loadAllSchoolClasses();
        }
    }, [showAllClasses]);

    useEffect(() => {
        if (selectedClass) {
            loadReportData();

            // Check if dateRange is the default (last 7 days)
            const [start, end] = dateRange || [];
            const defaultStart = dayjs().subtract(6, 'day');
            const defaultEnd = dayjs();
            const isDefault = start?.isSame(defaultStart, 'day') && end?.isSame(defaultEnd, 'day');

            // Build URL with or without query params
            let url = `/web/ui/view/reports/activityReport/${selectedClass}`;
            if (!isDefault && start && end) {
                const params = new URLSearchParams();
                params.set('startDate', start.format('YYYY-MM-DD'));
                params.set('endDate', end.format('YYYY-MM-DD'));
                url += `?${params.toString()}`;
            }

            navigate(url, { replace: true });
        }
    }, [selectedClass, dateRange]);

    const handleStudentNameClick = (studentRecord) => {
        const [start, end] = dateRange || [];
        const params = new URLSearchParams();

        if (start) params.set('startDate', start.format('YYYY-MM-DD'));
        if (end) params.set('endDate', end.format('YYYY-MM-DD'));

        navigate(`/web/ui/view/reports/activityReport/${selectedClass}/student/${studentRecord.studentId}?${params.toString()}`);
    };

    const handleShowAllClassesChange = (checked) => {
        setShowAllClasses(checked);
        if (!checked) {
            setSelectedClass(null);
        }
    };

    const getAvailableClasses = () => {
        return showAllClasses ? allSchoolClasses : teacherClasses;
    };

    const renderClassSelectDropdown = (menu) => (
        <div>
            <div style={{
                padding: '8px 12px',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
            }}>
                <Switch
                    checked={showAllClasses}
                    onChange={handleShowAllClassesChange}
                    size="small"
                />
                <span style={{ fontSize: '14px' }}>Show all classes</span>
            </div>
            {menu}
        </div>
    );

    const allColumns = {
        student: {
            title: 'Student',
            dataIndex: 'student',
            key: 'student',
            render: (text, record) => (<AntdButton type={'link'} onClick={() => handleStudentNameClick(record)}><strong>{text}</strong> <ArrowForwardRounded /></AntdButton>)
        },
        sessionsStarted: {
            title: 'Sessions Started',
            dataIndex: 'sessionsStarted',
            key: 'sessionsStarted',
        },
        lastLogin: {
            title: 'Last Login',
            dataIndex: 'lastLogin',
            key: 'lastLogin',
        },
        exercisesComplete: {
            title: 'Exercises Complete',
            dataIndex: 'exercisesComplete',
            key: 'exercisesComplete',
            sorter: (a, b) => a.exercisesComplete - b.exercisesComplete,
        },
        walkthroughsComplete: {
            title: 'Walkthroughs Complete',
            dataIndex: 'walkthroughsComplete',
            key: 'walkthroughsComplete',
            sorter: (a, b) => a.walkthroughsComplete - b.walkthroughsComplete,
        },
        quizzesComplete: {
            title: 'Section Quizzes Complete',
            dataIndex: 'quizzesComplete',
            key: 'quizzesComplete',
            sorter: (a, b) => a.quizzesComplete - b.quizzesComplete,
        },
        quizQuestions: {
            title: 'Questions Complete',
            dataIndex: 'quizQuestions',
            key: 'quizQuestions',
            sorter: (a, b) => a.quizQuestions - b.quizQuestions,
        },
        testsComplete: {
            title: `${brandConfig.testsName} Complete`,
            dataIndex: 'testsComplete',
            key: 'testsComplete',
            sorter: (a, b) => a.testsComplete - b.testsComplete,
        },
        scorchersComplete: {
            title: 'Scorchers Complete',
            dataIndex: 'scorchersComplete',
            key: 'scorchersComplete',
            sorter: (a, b) => a.scorchersComplete - b.scorchersComplete,
        },
    };

    const selectedKeys = visibleColumns.length > 0 ? visibleColumns : Object.keys(allColumns);
    const columns = ['student', ...selectedKeys.filter(key => key !== 'student')].map((key) => allColumns[key]);

    const generateReadableLastLogin = (epochTime) => {
        if (!epochTime) return 'No login recorded'
        const date = new Date(epochTime);
        return date.toLocaleString();
    };

    const studentFilterOptions = [
        { label: 'Logged in', value: 'loggedIn' },
        { label: 'Completed an activity', value: 'completedActivity' },
    ];

    const tableData = reportData
        .map((item, index) => {
            const { name, countSessions, countExercises, lastLogin, countQuestions, countWalkthroughs, countTests, countScorchers, countQuizzes, studentId } = item;

            return {
                key: index,
                student: name,
                lastLogin: generateReadableLastLogin(lastLogin),
                sessionsStarted: countSessions,
                quizzesComplete: countQuizzes,
                quizQuestions: countQuestions,
                exercisesComplete: countExercises,
                walkthroughsComplete: countWalkthroughs,
                testsComplete: countTests,
                scorchersComplete: countScorchers,
                studentId: studentId,
                activitiesComplete: countTests + countScorchers + countWalkthroughs,
                rawLastLogin: lastLogin,
                rawActivityCounts: [countExercises, countSessions, countQuestions, countWalkthroughs, countTests, countScorchers],
            };
        })
        .filter((student) => {
            if (studentFilters.length === 0) return true;

            let passes = true;

            if (studentFilters.includes('loggedIn')) {
                passes = passes && !!student.rawLastLogin;
            }

            if (studentFilters.includes('completedActivity')) {
                passes = passes && student.rawActivityCounts.some(count => count > 0);
            }

            return passes;
        });

    const filteredTableData = selectedStudent
        ? tableData.filter((record) => record.student === selectedStudent)
        : tableData;

    const studentsList = reportData.map((item) => ({
        label: item.name,
        value: item.name,
    }));

    const handleStudentFilterChange = (value) => {
        setSelectedStudent(value);
    };

    const handleAIReportGeneratorClick = () => {
        setAIReportGeneratorDialogVisible(true);
    };

    const handleDialogClose = () => {
        setAIReportGeneratorDialogVisible(false);
    };

    const BreadcrumbBar = useBreadcrumbComponent({ dropLast: true });

    const getExportFileName = () => {
        if (!selectedClass || !getAvailableClasses().length) {
            return 'activity_report.csv';
        }
        const currentClass = getAvailableClasses().find(c => c.value === selectedClass);
        const className = currentClass ? currentClass.label.replace(/\s+/g, '-') : 'class';
        return `${className}_activity_report.csv`;
    };

    if(!user || typeof user.getDateFormatString !== 'function'){
        return(
            <CircularLoader />
        )
    }

    return (
        <div>
            <BreadcrumbBar />
            <PageHeader
                title={'Activity Report'}
                buttons={
                    <>
                        <ExportToCsvButton
                            data={filteredTableData}
                            columns={columns}
                            fileName={getExportFileName()}
                            disabled={filteredTableData.length === 0}
                        />
                    </>
                }
            />
            <TableFilterControls
                filterContent={
                    <TableFilterControlsForm>
                        <TableFilterControlsSection showDivider={false}>
                            <Form.Item label={<span>Class</span>} htmlFor="class-select">
                                <Select
                                    id="class-select"
                                    placeholder="Select class"
                                    options={getAvailableClasses()}
                                    value={selectedClass}
                                    onChange={setSelectedClass}
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.label.toLowerCase().includes(input.toLowerCase())
                                    }
                                    popupRender={renderClassSelectDropdown}
                                />
                            </Form.Item>

                            <Form.Item label={<span>Student</span>} name="student">
                                <Select
                                    placeholder="Select student"
                                    options={studentsList}
                                    value={selectedStudent}
                                    onChange={handleStudentFilterChange}
                                    showSearch
                                    allowClear
                                    filterOption={(input, option) =>
                                        option.label.toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </Form.Item>

                            <Form.Item label={<span>Date range</span>} htmlFor="date-range-picker">
                                <RangePicker
                                    id="date-range-picker"
                                    presets={Object.entries(presetRanges).map(([label, range]) => ({
                                        label,
                                        value: range,
                                    }))}
                                    value={dateRange}
                                    onChange={setDateRange}
                                    format={user.getDateFormatString()}
                                    style={{width: '100%'}}
                                />
                            </Form.Item>

                            <Form.Item label={<span>Columns</span>} name='columns'>
                                <Select
                                    mode="multiple"
                                    placeholder="Select visible columns"
                                    options={columnOptions}
                                    value={visibleColumns}
                                    onChange={setVisibleColumns}
                                    allowClear={true}
                                    maxTagCount={0}
                                    maxTagPlaceholder={`${visibleColumns.length} selected`}
                                />
                            </Form.Item>

                            <Form.Item label={<span>Students who</span>} htmlFor="student-filters-select">
                                <Select
                                    id="student-filters-select"
                                    mode="multiple"
                                    placeholder="Filter by student activity"
                                    options={studentFilterOptions}
                                    value={studentFilters}
                                    onChange={setStudentFilters}
                                    allowClear
                                />
                            </Form.Item>
                        </TableFilterControlsSection>
                    </TableFilterControlsForm>

                }
            >
            </TableFilterControls>
            <TableAntD
                dynamicWidth={true}
                columnData={columns}
                scroll={{ x: true }}
                dataSource={filteredTableData}
                locale={{ emptyText: 'Select a class with students' }}
                loading={loading}
                className={'ch-reports-table'}
            />
        </div>
    );
};

export default ActivityReport;